/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { apiGet } from '../../utils/apiClient';
import { SuccessResponse } from '../../types/CourseStageAccordion.type';
import { Dayjs } from 'dayjs';
import {
  serviceColors,
  serviceTypeColors,
  userTypeColors,
} from '../../utils/colorHandler';
import ColorChip from '../../components/common/ColorChip';
import TableLoader from '../../components/common/TableLoader';
import {
  EmptyValue,
  isAnonymousUserId,
} from '../../components/common/EmptyValue';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { saveAs } from 'file-saver';
import { notifyAPIClient } from '../../utils/toast';
import {
  rowHoverBg,
  selectedRowBg,
  subtleFieldBg,
  walkInRowBg,
} from '../../utils/rowHighlight';
import { useIsMobile } from '../../hooks/useResponsive';
import { useTranslation } from '../../i18n';

// One label/value row inside a mobile registration card.
const CardField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right', minWidth: 0 }}>{children}</Box>
  </Box>
);

const RegistrationTable: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalRevenueByPaymentMethod, setTotalRevenueByPaymentMethod] =
    useState<any[]>([]);

  const [numberOfInvoiced, setNumberOfInvoiced] = useState(0);
  const [numberOfNotInvoiced, setNumberOfNotInvoiced] = useState(0);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  // Export registrations to CSV
  const [exportLoading, setExportLoading] = useState(false);

  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch registrations with services
      const startDateParam =
        startDate && startDate.isValid()
          ? `&startDate=${startDate.format('YYYY-MM-DD')}`
          : '';
      const endDateParam =
        endDate && endDate.isValid()
          ? `&endDate=${endDate.format('YYYY-MM-DD')}`
          : '';

      const registrationsData = await apiGet<SuccessResponse>(
        `/report/get-registration-report?page=${page + 1}&limit=${rowsPerPage}${startDateParam}${endDateParam}`,
      );
      setRegistrations(registrationsData.data);
      setTotalItems(registrationsData.metadata.totalItems);
      setTotalRevenue(registrationsData.metadata.totalRevenue);
      // console.log(registrationsData.metadata.revenueByPaymentMethod);
      setTotalRevenueByPaymentMethod(
        registrationsData.metadata.revenueByPaymentMethod,
      );
      setNumberOfInvoiced(registrationsData.metadata.numberOfInvoiced);
      setNumberOfNotInvoiced(registrationsData.metadata.numberOfNotInvoiced);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatPaymentMethod = (method: string) => {
    return method
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  };

  const handleExportToCSV = async () => {
    setExportLoading(true);
    try {
      const startDateParam =
        startDate && startDate.isValid()
          ? `startDate=${startDate.format('YYYY-MM-DD')}`
          : '';
      const endDateParam =
        endDate && endDate.isValid()
          ? `endDate=${endDate.format('YYYY-MM-DD')}`
          : '';

      const queryParams = [startDateParam, endDateParam]
        .filter((param) => param)
        .join('&');

      const response = await apiGet<Blob>(
        `/report/export-registration-report${queryParams ? `?${queryParams}` : ''}`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([response as any], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const dateRangeLabel =
        startDate && endDate
          ? `${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}`
          : startDate
            ? `from_${startDate.format('YYYY-MM-DD')}`
            : endDate
              ? `until_${endDate.format('YYYY-MM-DD')}`
              : 'all';

      saveAs(blob, `registration_report_${dateRangeLabel}.xlsx`);
    } catch (error) {
      console.error('Export data failed:', error);
      notifyAPIClient(t('report.reg_exportFailed'));
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, startDate, endDate]);

  const formatVND = (value: number) =>
    new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(value);

  // Net cost = price (+ child price) − discount, floored at 0.
  const netCost = (r: any) => {
    const cost =
      r.price + (r.priceForChild || 0) - (r.discountPrice || 0);
    return cost > 0 ? cost : 0;
  };

  const isMobile = useIsMobile();

  const totalInvoiced = numberOfInvoiced + numberOfNotInvoiced;
  const formatPercent = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(totalInvoiced === 0 ? 0 : value / totalInvoiced);

  // Copy a metric's raw numeric value to the clipboard (unformatted, so it
  // pastes cleanly into spreadsheets/invoices).
  const handleCopyMetric = async (value: number, label: string) => {
    try {
      await navigator.clipboard.writeText(String(value));
      notifyAPIClient(t('report.reg_copied', { label }));
    } catch {
      notifyAPIClient(t('report.reg_unableToCopy'));
    }
  };

  const renderCopyButton = (value: number, label: string) => (
    <Tooltip title={t('report.reg_copyLabel', { label })}>
      <IconButton
        size="small"
        aria-label={t('report.reg_copyLabel', { label })}
        onClick={() => handleCopyMetric(value, label)}
        sx={{ ml: 0.5, p: 0.25 }}
      >
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          lg: 'minmax(0, 2.5fr) minmax(300px, 1fr)',
        },
        gap: 3,
        alignItems: 'stretch',
      }}
    >
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
        <Typography variant="h6" fontWeight="bold">
          {t('report.reg_registrationList')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              label={t('report.reg_paymentStartDate')}
              value={startDate}
              onChange={(newDate) => setStartDate(newDate)}
              slotProps={{
                textField: {
                  size: 'small',
                },
                field: {
                  clearable: true,
                  onClear: () => setStartDate(null),
                },
              }}
              maxDate={endDate || undefined}
            />
            <DatePicker
              format="DD/MM/YYYY"
              label={t('report.reg_paymentEndDate')}
              value={endDate}
              onChange={(newDate) => setEndDate(newDate)}
              slotProps={{
                textField: {
                  size: 'small',
                },
                field: {
                  clearable: true,
                  onClear: () => setEndDate(null),
                },
              }}
              minDate={startDate || undefined}
            />
          </LocalizationProvider>

          <Tooltip title={t('report.reg_downloadReport')}>
            <IconButton
              onClick={handleExportToCSV}
              loading={exportLoading}
              disabled={
                registrations.length > 0 && !exportLoading ? false : true
              }
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {isMobile ? (
        <Stack spacing={1.5} sx={{ p: 1.5 }}>
          {registrations.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                {t('report.reg_noRegistrationsFound')}
              </Typography>
            </Box>
          ) : (
            registrations.map((registration) => {
              const isAnonymous = isAnonymousUserId(registration.userId);
              return (
                <Card
                  key={registration.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    ...(isAnonymous && {
                      backgroundColor: walkInRowBg,
                      borderLeft: '3px solid #f0ad4e',
                    }),
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {registration.user.lastName}{' '}
                        {isAnonymous && registration.walkInCustomerName
                          ? registration.walkInCustomerName
                          : registration.user.firstName}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mt: 0.25 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('report.reg_id')} {registration.userId}
                        </Typography>
                        {isAnonymous && (
                          <Chip
                            label={t('report.reg_anonymous')}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        flexShrink: 0,
                        fontWeight: 'bold',
                        color: registration.isInvoiced ? '#4caf50' : '#f44336',
                      }}
                    >
                      {registration.isInvoiced
                        ? t('report.reg_invoiced')
                        : t('report.reg_notInvoiced')}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <CardField label={t('report.reg_type')}>
                    <ColorChip
                      label={registration.user.userType}
                      palette={userTypeColors}
                    />
                  </CardField>
                  <CardField label={t('report.reg_product')}>
                    <ColorChip
                      label={registration.serviceType}
                      palette={serviceColors}
                    />
                  </CardField>
                  <CardField label={t('report.reg_service')}>
                    <ColorChip
                      label={registration.subtype}
                      palette={serviceTypeColors}
                    />
                  </CardField>
                  <CardField label={t('report.reg_details')}>
                    <ColorChip label={registration.subsubtype.name} />
                  </CardField>
                  <CardField label={t('report.reg_cost')}>
                    {formatVND(netCost(registration))}
                  </CardField>
                  <CardField label={t('report.reg_discount')}>
                    {formatVND(registration.discountPrice || 0)}
                  </CardField>
                  <CardField label={t('report.reg_quantity')}>
                    {registration.subsubtype.serviceFor === 'GROUP'
                      ? (registration.amount || 0) +
                        (registration.subAmount || 0)
                      : registration.amount || 0}
                  </CardField>
                  <CardField label={t('report.reg_startEnd')}>
                    {new Date(registration.sos).toLocaleDateString()} –{' '}
                    {new Date(registration.eos).toLocaleDateString()}
                  </CardField>
                  <CardField label={t('report.reg_paymentDate')}>
                    {new Date(registration.createdAt).toLocaleDateString()}
                  </CardField>
                  <CardField label={t('report.reg_paymentMethod')}>
                    {formatPaymentMethod(registration.paymentMethod)}
                  </CardField>
                </Card>
              );
            })
          )}
        </Stack>
      ) : (
      <TableContainer>
        <Table size="small" padding="checkbox">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#4da7db' }}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_userId')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_lastName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_firstName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_preferredName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_businessName')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_contactInformation')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_product')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_type')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_details')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_cost')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_discountAmount')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_quantity')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_startDate')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_endDate')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_paymentDate')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_paymentMethod')}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Typography fontWeight="bold" color="#fff">
                  {t('report.reg_invoiced')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableLoader colSpan={17} />
            ) : (
              <>
            {registrations.map((registrations) => {
              // Anonymous walk-in customer (no full info collected) — muted,
              // matching the main Registration table.
              const isAnonymous = isAnonymousUserId(registrations.userId);
              return (
              <TableRow
                key={registrations.id}
                sx={{
                  '&:hover': { backgroundColor: rowHoverBg },
                  backgroundColor:
                    highlightedRow === `data_${registrations.id}`
                      ? selectedRowBg
                      : isAnonymous
                        ? walkInRowBg
                        : 'inherit',
                  ...(isAnonymous && {
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  }),
                }}
                onClick={() => {
                  if (highlightedRow === `data_${registrations.id}`) {
                    setHighlightedRow(null);
                  } else {
                    setHighlightedRow(`data_${registrations.id}`);
                  }
                }}
              >
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    ...(isAnonymous && { borderLeft: '3px solid #f0ad4e' }),
                  }}
                >
                  {isAnonymous ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{registrations.userId}</span>
                      <Chip
                        label={t('report.reg_anonymous')}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Stack>
                  ) : (
                    registrations.userId
                  )}
                </TableCell>
                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                >{`${registrations.user.lastName}`}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {isAnonymous && registrations.walkInCustomerName
                    ? registrations.walkInCustomerName
                    : registrations.user.firstName}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {registrations.user.preferredName || <EmptyValue />}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {registrations.user.userType !== 'BUSINESS' ? (
                    <EmptyValue />
                  ) : (
                    registrations.user.businessName || <EmptyValue />
                  )}
                </TableCell>
                <TableCell>
                  <ColorChip
                    label={registrations.user.userType}
                    palette={userTypeColors}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <ColorChip
                    label={registrations.serviceType}
                    palette={serviceColors}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <ColorChip
                    label={registrations.subtype}
                    palette={serviceTypeColors}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <ColorChip label={registrations.subsubtype.name} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                    maximumFractionDigits: 0,
                  }).format(
                    // registrations.price - (registrations.discountPrice || 0),
                    registrations.price +
                      (registrations.priceForChild || 0) -
                      (registrations.discountPrice || 0) >
                      0
                      ? registrations.price +
                          (registrations.priceForChild || 0) -
                          (registrations.discountPrice || 0)
                      : 0,
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND',
                    maximumFractionDigits: 0,
                  }).format(registrations.discountPrice || 0)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {registrations.subsubtype.serviceFor === 'GROUP'
                    ? (registrations.amount || 0) +
                      (registrations.subAmount || 0)
                    : registrations.amount || 0}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div>{new Date(registrations.sos).toLocaleDateString()}</div>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div>{new Date(registrations.eos).toLocaleDateString()}</div>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Date(registrations.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div>{formatPaymentMethod(registrations.paymentMethod)}</div>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div
                    style={{
                      color: registrations.isInvoiced ? '#4caf50' : '#f44336',
                      fontWeight: 'bold',
                    }}
                  >
                    {registrations.isInvoiced
                      ? t('report.reg_invoiced')
                      : t('report.reg_notInvoiced')}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
            {registrations.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No registrations found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </Card>

      {/* Revenue summary — always visible beside the registration list */}
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {t('report.reg_totalRevenue')}:{' '}
              <Box component="span" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                {formatVND(totalRevenue)}
              </Box>
            </Typography>
            {renderCopyButton(totalRevenue, t('report.reg_totalRevenue'))}
          </Box>

          <Box
            sx={{
              p: 1.5,
              backgroundColor: subtleFieldBg,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
              {t('report.reg_revenueByPaymentMethod')}
            </Typography>
            {totalRevenueByPaymentMethod.length > 0 ? (
              totalRevenueByPaymentMethod.map((item) => (
                <Box
                  key={item.paymentMethod}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 0.35,
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">
                    {formatPaymentMethod(item.paymentMethod)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      {formatVND(item.totalRevenue)}
                    </Typography>
                    {renderCopyButton(
                      item.totalRevenue,
                      formatPaymentMethod(item.paymentMethod),
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('report.reg_noDataAvailable')}
              </Typography>
            )}

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mt: 1.5, mb: 0.75 }}
            >
              {t('report.reg_invoicedStatus')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: 0.35,
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">
                {t('report.reg_invoiced')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  {numberOfInvoiced} ({formatPercent(numberOfInvoiced)})
                </Typography>
                {renderCopyButton(numberOfInvoiced, t('report.reg_invoiced'))}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: 0.35,
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">
                {t('report.reg_notInvoiced')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  {numberOfNotInvoiced} ({formatPercent(numberOfNotInvoiced)})
                </Typography>
                {renderCopyButton(
                  numberOfNotInvoiced,
                  t('report.reg_notInvoiced'),
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default RegistrationTable;
